import React, { useState } from 'react';
import { supabase } from '../services/supabase';

interface SimpleAuthProps {
  onAuthSuccess: (userId: string) => void;
}

/**
 * SimpleAuth - Temporary authentication component for testing
 * Allows creating/selecting a test user to work with real database
 */
export const SimpleAuth: React.FC<SimpleAuthProps> = ({ onAuthSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [telegramId, setTelegramId] = useState('');

  const handleCreateUser = async () => {
    if (!firstName.trim() || !telegramId.trim()) {
      setError('Заполните все поля');
      return;
    }

    const telegramIdNum = parseInt(telegramId);
    if (isNaN(telegramIdNum)) {
      setError('Telegram ID должен быть числом');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if user already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('*')
        .eq('telegram_id', telegramIdNum)
        .single();

      if (existingUser) {
        console.log('User already exists:', existingUser);
        onAuthSuccess((existingUser as any).id);
        return;
      }

      // Create new user
      const { data: newUser, error: createError } = await supabase
        .from('users')
        .insert({
          telegram_id: telegramIdNum,
          first_name: firstName.trim(),
          language_code: 'ru'
        } as any)
        .select()
        .single();

      if (createError) {
        throw createError;
      }

      console.log('Created new user:', newUser);
      onAuthSuccess((newUser as any).id);

    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'Ошибка авторизации');
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAuth = async () => {
    // Quick auth with predefined user
    const quickTelegramId = 123456789;
    const quickFirstName = 'Тестовый Пользователь';

    setIsLoading(true);
    setError(null);

    try {
      // Check if user exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('*')
        .eq('telegram_id', quickTelegramId)
        .single();

      if (existingUser) {
        onAuthSuccess((existingUser as any).id);
        return;
      }

      // Create quick user
      const { data: newUser, error: createError } = await supabase
        .from('users')
        .insert({
          telegram_id: quickTelegramId,
          first_name: quickFirstName,
          language_code: 'ru'
        } as any)
        .select()
        .single();

      if (createError) {
        throw createError;
      }

      onAuthSuccess((newUser as any).id);

    } catch (err: any) {
      console.error('Quick auth error:', err);
      setError(err.message || 'Ошибка быстрой авторизации');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="simple-auth min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">🏋️‍♂️ Дневник тренировок</h1>
          <p className="text-gray-600">Тестовая авторизация</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Quick Auth */}
        <div className="mb-6">
          <button
            onClick={handleQuickAuth}
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Авторизация...' : 'Быстрая авторизация'}
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Создаст тестового пользователя с ID 123456789
          </p>
        </div>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">или</span>
          </div>
        </div>

        {/* Custom User */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Имя
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ваше имя"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telegram ID
            </label>
            <input
              type="number"
              value={telegramId}
              onChange={(e) => setTelegramId(e.target.value)}
              placeholder="123456789"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Любое уникальное число для тестирования
            </p>
          </div>

          <button
            onClick={handleCreateUser}
            disabled={isLoading}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Создание...' : 'Создать пользователя'}
          </button>
        </div>

        <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-yellow-800 text-sm">
            <strong>Режим тестирования:</strong> Это временная авторизация для тестирования с реальной базой данных. 
            В финальной версии будет использоваться Telegram WebApp авторизация.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SimpleAuth;